import { useEffect, useState } from "react";
import { NavLink, Link, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, ShoppingBag, Package, FolderTree, Layers, Warehouse, Users, Percent, Star, Megaphone, Image, Menu as MenuIcon, FileText, PenLine, BarChart3, Search, Palette, CreditCard, Truck, Settings, ShieldCheck, LogOut, Store, Bell, Upload, Ruler, Globe, Activity, Video, Instagram, Archive, Sparkles, BellRing, Contact } from "lucide-react";
import api, { setToken } from "@/api";

const NAV = [
  { to: "/admin", icon: LayoutDashboard, label: "Dashboard", end: true },
  { to: "/admin/orders", icon: ShoppingBag, label: "Orders" },
  { to: "/admin/products", icon: Package, label: "Products" },
  { to: "/admin/bulk", icon: Upload, label: "Bulk Upload" },
  { to: "/admin/categories", icon: FolderTree, label: "Categories" },
  { to: "/admin/collections", icon: Layers, label: "Collections" },
  { to: "/admin/size-charts", icon: Ruler, label: "Size Charts" },
  { to: "/admin/inventory", icon: Warehouse, label: "Inventory" },
  { to: "/admin/customers", icon: Users, label: "Customers" },
  { to: "/admin/discounts", icon: Percent, label: "Discounts" },
  { to: "/admin/reviews", icon: Star, label: "Reviews" },
  { to: "/admin/notify", icon: Bell, label: "Notify Requests" },
  { to: "/admin/banners", icon: Megaphone, label: "Banners" },
  { to: "/admin/menus", icon: MenuIcon, label: "Navigation" },
  { to: "/admin/homepage", icon: Palette, label: "Homepage" },
  { to: "/admin/animations", icon: Sparkles, label: "Homepage Animations" },
  { to: "/admin/pages", icon: FileText, label: "Pages" },
  { to: "/admin/blog", icon: PenLine, label: "Blog" },
  { to: "/admin/media", icon: Image, label: "Media Library" },
  { to: "/admin/videos", icon: Video, label: "Videos" },
  { to: "/admin/seo", icon: Search, label: "SEO & Redirects" },
  { to: "/admin/seo-health", icon: Activity, label: "SEO Health" },
  { to: "/admin/reports", icon: BarChart3, label: "Reports" },
  { to: "/admin/marketing", icon: Globe, label: "Marketing" },
  { to: "/admin/campaigns", icon: Megaphone, label: "Campaigns" },
  { to: "/admin/social", icon: Instagram, label: "Social Sync" },
  { to: "/admin/notifications", icon: BellRing, label: "Notifications" },
  { to: "/admin/customer-db", icon: Contact, label: "Customer Database" },
  { to: "/admin/payments", icon: CreditCard, label: "Payments" },
  { to: "/admin/shipping", icon: Truck, label: "Shipping & Delivery" },
  { to: "/admin/settings", icon: Settings, label: "Store Settings" },
  { to: "/admin/users", icon: ShieldCheck, label: "Users & Roles" },
  { to: "/admin/backups", icon: Archive, label: "Backups & Export" },
];

export default function AdminLayout() {
  const [admin, setAdmin] = useState(null);
  const [checking, setChecking] = useState(true);
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/auth/me").then(({ data }) => {
      if (["super_admin", "admin", "catalog_manager", "order_manager", "support", "marketing"].includes(data.role)) setAdmin(data);
      setChecking(false);
    }).catch(() => setChecking(false));
  }, []);

  const doLogin = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/auth/login", form);
      if (!["super_admin", "admin", "catalog_manager", "order_manager", "support", "marketing"].includes(data.user.role)) {
        setError("This account does not have admin access.");
        setBusy(false);
        return;
      }
      setToken(data.token);
      setAdmin(data.user);
    } catch (err) {
      setError(err.friendlyMessage);
    } finally { setBusy(false); }
  };

  if (checking) return <div className="min-h-screen bg-[#121212] flex items-center justify-center"><p className="font-serif text-2xl text-[#D4AF37] animate-pulse">TARISHQ Admin</p></div>;

  if (!admin) return (
    <div className="min-h-screen bg-[#121212] flex items-center justify-center p-4">
      <form onSubmit={doLogin} className="w-full max-w-sm bg-white p-8" data-testid="admin-login-form">
        <p className="font-serif text-3xl tracking-[0.1em] text-center">TARISHQ</p>
        <p className="text-[10px] uppercase tracking-[0.4em] text-center text-[#8C7A5E] mt-1 mb-8">Admin Console</p>
        {error && <p className="text-sm text-[#8B1E21] bg-[#8B1E21]/10 p-3 mb-4" data-testid="admin-login-error">{error}</p>}
        <input type="email" required placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="admin-email" className="tq-input mb-3" />
        <input type="password" required placeholder="Password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="admin-password" className="tq-input mb-5" />
        <button type="submit" disabled={busy} data-testid="admin-login-btn" className="tq-btn-dark w-full">{busy ? "Signing in…" : "Sign In"}</button>
        <Link to="/" className="block text-center text-xs text-[#8C7A5E] underline mt-4">← Back to store</Link>
      </form>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F4EFE6] flex" data-testid="admin-panel">
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-60 bg-[#121212] text-[#FAF8F5] flex flex-col transition-transform lg:translate-x-0 ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}`}>
        <div className="p-5 border-b border-white/10">
          <p className="font-serif text-xl tracking-[0.15em]">TARISHQ</p>
          <p className="text-[9px] uppercase tracking-[0.4em] text-[#C5A059]">Admin Console</p>
        </div>
        <nav className="flex-1 overflow-y-auto py-3" data-testid="admin-nav">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setSidebarOpen(false)}
              data-testid={`admin-nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) => `flex items-center gap-3 px-5 py-2.5 text-[13px] transition-colors ${isActive ? "bg-[#D4AF37]/15 text-[#D4AF37] border-r-2 border-[#D4AF37]" : "text-white/60 hover:text-white hover:bg-white/5"}`}>
              <n.icon size={16} /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10 space-y-2">
          <Link to="/" className="flex items-center gap-3 px-2 py-2 text-[13px] text-white/60 hover:text-white"><Store size={15} /> View Store</Link>
          <button onClick={() => { setToken(null); setAdmin(null); navigate("/admin"); }} data-testid="admin-logout-btn"
            className="flex items-center gap-3 px-2 py-2 text-[13px] text-white/60 hover:text-[#D4AF37] w-full"><LogOut size={15} /> Logout ({admin.name.split(" ")[0]})</button>
        </div>
      </aside>
      {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />}
      <div className="flex-1 min-w-0">
        <div className="lg:hidden sticky top-0 z-30 bg-[#121212] text-white flex items-center justify-between p-4">
          <button onClick={() => setSidebarOpen(true)} data-testid="admin-mobile-menu-btn" aria-label="Open menu"><MenuIcon size={20} /></button>
          <p className="font-serif tracking-[0.15em]">TARISHQ ADMIN</p>
          <span className="w-5" />
        </div>
        <main className="p-4 sm:p-6 lg:p-8 max-w-[1400px]">
          <Outlet context={{ admin }} />
        </main>
      </div>
    </div>
  );
}
